import React, { useState, useEffect } from 'react';
import './AdminUsers.css';

function AdminUsers() {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [message, setMessage] = useState(null);

  // New user form
  const [newUsername, setNewUsername] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [newIsAdmin, setNewIsAdmin] = useState(false);
  const [creating, setCreating] = useState(false);

  // Edit state: { userId, field, value }
  const [editRow, setEditRow] = useState(null);
  const [editValue, setEditValue] = useState('');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    fetchUsers();
  }, []);

  const fetchUsers = async () => {
    try {
      const res = await fetch('/api/admin/users', { credentials: 'include' });
      const data = await res.json();
      if (Array.isArray(data)) setUsers(data);
    } catch (e) {
      setMessage({ type: 'error', text: 'Failed to load users' });
    } finally {
      setLoading(false);
    }
  };

  const showMsg = (type, text) => {
    setMessage({ type, text });
    setTimeout(() => setMessage(null), 4000);
  };

  const handleCreate = async (e) => {
    e.preventDefault();
    setCreating(true);
    try {
      const res = await fetch('/api/admin/users', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ username: newUsername, password: newPassword, is_admin: newIsAdmin })
      });
      const data = await res.json();
      if (data.success) {
        showMsg('success', `User "${newUsername}" created`);
        setNewUsername('');
        setNewPassword('');
        setNewIsAdmin(false);
        fetchUsers();
      } else {
        showMsg('error', data.error || 'Failed to create user');
      }
    } catch (e) {
      showMsg('error', 'Connection error');
    } finally {
      setCreating(false);
    }
  };

  const startEdit = (userId, field, current) => {
    setEditRow({ userId, field });
    setEditValue(current || '');
  };

  const cancelEdit = () => {
    setEditRow(null);
    setEditValue('');
  };

  const commitEdit = async () => {
    if (!editRow) return;
    setSaving(true);
    const payload = { [editRow.field]: editRow.field === 'is_admin' ? editValue : editValue };
    try {
      const res = await fetch(`/api/admin/users/${editRow.userId}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(payload)
      });
      const data = await res.json();
      if (data.success) {
        showMsg('success', 'Updated');
        setEditRow(null);
        fetchUsers();
      } else {
        showMsg('error', data.error || 'Update failed');
      }
    } catch (e) {
      showMsg('error', 'Connection error');
    } finally {
      setSaving(false);
    }
  };

  const handleToggleAdmin = async (userId, currentIsAdmin) => {
    try {
      const res = await fetch(`/api/admin/users/${userId}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ is_admin: !currentIsAdmin })
      });
      const data = await res.json();
      if (data.success) {
        fetchUsers();
      } else {
        showMsg('error', data.error || 'Failed to update');
      }
    } catch (e) {
      showMsg('error', 'Connection error');
    }
  };

  const handleDelete = async (userId, username) => {
    if (!window.confirm(`Delete user "${username}"? This cannot be undone.`)) return;
    try {
      const res = await fetch(`/api/admin/users/${userId}`, {
        method: 'DELETE',
        credentials: 'include'
      });
      const data = await res.json();
      if (data.success) {
        showMsg('success', `User "${username}" deleted`);
        fetchUsers();
      } else {
        showMsg('error', data.error || 'Delete failed');
      }
    } catch (e) {
      showMsg('error', 'Connection error');
    }
  };

  if (loading) return <div className="au-loading">Loading users...</div>;

  return (
    <div className="au-container">
      <h1>User Management</h1>

      {message && (
        <div className={`au-message ${message.type}`}>{message.text}</div>
      )}

      {/* Create user */}
      <div className="au-card">
        <h3>Create New User</h3>
        <form className="au-create-form" onSubmit={handleCreate}>
          <input
            type="text"
            placeholder="Username"
            value={newUsername}
            onChange={e => setNewUsername(e.target.value)}
            className="au-input"
          />
          <input
            type="password"
            placeholder="Password"
            value={newPassword}
            onChange={e => setNewPassword(e.target.value)}
            className="au-input"
          />
          <label className="au-admin-toggle">
            <input
              type="checkbox"
              checked={newIsAdmin}
              onChange={e => setNewIsAdmin(e.target.checked)}
            />
            Admin
          </label>
          <button
            type="submit"
            className="btn btn-primary"
            disabled={creating || !newUsername || !newPassword}
          >
            {creating ? 'Creating...' : 'Create User'}
          </button>
        </form>
      </div>

      {/* User list */}
      <div className="au-card">
        <h3>Users ({users.length})</h3>
        <table className="au-table">
          <thead>
            <tr>
              <th>ID</th>
              <th>Username</th>
              <th>Admin</th>
              <th>Created</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {users.map(u => (
              <tr key={u.user_id}>
                <td className="au-id">{u.user_id}</td>
                <td>
                  {editRow && editRow.userId === u.user_id && editRow.field === 'username' ? (
                    <div className="au-edit-inline">
                      <input
                        value={editValue}
                        onChange={e => setEditValue(e.target.value)}
                        className="au-input-sm"
                        autoFocus
                      />
                      <button className="au-btn-save" onClick={commitEdit} disabled={saving}>
                        {saving ? '...' : 'Save'}
                      </button>
                      <button className="au-btn-cancel" onClick={cancelEdit}>Cancel</button>
                    </div>
                  ) : (
                    <span
                      className="au-username"
                      title="Click to edit"
                      onClick={() => startEdit(u.user_id, 'username', u.username)}
                    >
                      {u.username}
                    </span>
                  )}
                </td>
                <td>
                  <div
                    className={`au-toggle ${u.is_admin ? 'on' : ''}`}
                    onClick={() => handleToggleAdmin(u.user_id, u.is_admin)}
                    title={u.is_admin ? 'Admin — click to demote' : 'User — click to promote'}
                  >
                    <div className="au-toggle-thumb" />
                  </div>
                </td>
                <td className="au-date">{u.created_at ? u.created_at.split('T')[0] : '—'}</td>
                <td className="au-actions">
                  {editRow && editRow.userId === u.user_id && editRow.field === 'password' ? (
                    <div className="au-edit-inline">
                      <input
                        type="password"
                        value={editValue}
                        onChange={e => setEditValue(e.target.value)}
                        className="au-input-sm"
                        placeholder="New password"
                        autoFocus
                      />
                      <button className="au-btn-save" onClick={commitEdit} disabled={saving}>
                        {saving ? '...' : 'Save'}
                      </button>
                      <button className="au-btn-cancel" onClick={cancelEdit}>Cancel</button>
                    </div>
                  ) : (
                    <>
                      <button
                        className="au-btn-action"
                        onClick={() => startEdit(u.user_id, 'password', '')}
                      >
                        Change Password
                      </button>
                      <button
                        className="au-btn-danger"
                        onClick={() => handleDelete(u.user_id, u.username)}
                      >
                        Delete
                      </button>
                    </>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

export default AdminUsers;
